<template>
  <div class="main-container p-3">
    <KidHeader :profileId="profileId" :serialNumber="serialNumber" />

    <!-- Back to Schedule List -->
    <div class="back-home text-left mb-3">
      <a class="text-black" href="javascript:void(0)" @click="$router.push({ path: '/schedule', query: { profileId: this.profileId, serialNumber: serialNumber } })">
        <i class="fas fa-arrow-left"></i> <b>Back to Schedules</b>
      </a>
    </div>

    <!-- Conflict Warning Alert -->
    <div v-if="conflictWarning.show" class="alert alert-danger alert-dismissible fade show" role="alert">
      <h6 class="alert-heading"><i class="fas fa-exclamation-triangle"></i> Schedule Conflicts Detected</h6>
      <ul class="mb-0">
        <li v-for="(conflict, index) in conflictWarning.conflicts" :key="index">
          {{ conflict.message }}
        </li>
      </ul>
      <button type="button" class="close" @click="conflictWarning.show = false">
        <span aria-hidden="true">&times;</span>
      </button>
    </div>

    <!-- Warning Alert (non-blocking) -->
    <div v-if="warningAlert.show" class="alert alert-warning alert-dismissible fade show" role="alert">
      <h6 class="alert-heading"><i class="fas fa-exclamation-circle"></i> Warning</h6>
      <ul class="mb-0">
        <li v-for="(warning, index) in warningAlert.warnings" :key="index">
          {{ warning.message }}
        </li>
      </ul>
      <button type="button" class="close" @click="warningAlert.show = false">
        <span aria-hidden="true">&times;</span>
      </button>
    </div>

    <!-- Loading Overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <p class="mt-2">{{ loadingMessage }}</p>
    </div>

    <!-- Schedule Form -->
    <div class="schedule-form mt-2">
      <div class="card">
        <div class="card-body">

          <!-- Schedule Label -->
          <div class="form-group mb-3 text-left">
            <label class="font-weight-bold">
              <i class="fas fa-tag"></i> Schedule Name <span class="text-danger">*</span>
            </label>
            <input
              autofocus
              type="text"
              class="form-control form-control-sm"
              v-model="currentSchedule.label"
              placeholder="e.g., School Time, Bedtime, Weekend Gaming"
              :class="{ 'is-invalid': errors.label }"
            />
            <small class="text-muted">Give your schedule a short descriptive name</small>
            <div v-if="errors.label" class="invalid-feedback">{{ errors.label }}</div>
          </div>

          <!-- Schedule Type Selector (compact) -->
          <div class="form-group mb-3 text-left">
            <label class="font-weight-bold d-block mb-2">
              <i class="fas fa-layer-group"></i> Schedule Type <span class="text-danger">*</span>
            </label>

            <div class="type-chips">
              <button
                type="button"
                class="type-chip"
                :class="{ active: currentSchedule.type === 'custom' }"
                @click="selectScheduleType('custom')"
                title="Custom - choose days manually"
              >
                <i class="fas fa-sliders-h"></i>
                <div class="chip-label">Custom</div>
              </button>

              <button
                type="button"
                class="type-chip"
                :class="{ active: currentSchedule.type === 'weekday' }"
                @click="selectScheduleType('weekday')"
                title="Mon - Fri"
              >
                <i class="fas fa-briefcase"></i>
                <div class="chip-label">Weekday</div>
              </button>

              <button
                type="button"
                class="type-chip"
                :class="{ active: currentSchedule.type === 'weekend' }"
                @click="selectScheduleType('weekend')"
                title="Sat - Sun"
              >
                <i class="fas fa-calendar-week"></i>
                <div class="chip-label">Weekend</div>
              </button>
            </div>
          </div>

          <!-- Day Selection (modern bubbles) -->
          <div class="form-group mb-3 text-left">
            <label class="font-weight-bold d-block mb-2">
              <i class="fas fa-calendar-day"></i> Select Days <span class="text-danger">*</span>
            </label>

            <div class="days-bubbles">
              <button
                type="button"
                v-for="day in allDays"
                :key="day.value"
                class="day-bubble"
                :class="{ active: currentSchedule.days.includes(day.value) }"
                @click="toggleDay(day.value)"
              >
                {{ day.short }}
              </button>
            </div>

            <div class="mt-2">
              <small v-if="errors.days" class="text-danger">{{ errors.days }}</small>
              <small v-else class="text-muted">Tap days to include them in the schedule.</small>
            </div>
          </div>

          <!-- Restriction Settings (Multiple) -->
          <div class="form-group mb-3 text-left">
            <label class="font-weight-bold d-block mb-2">
              <i class="fas fa-clock"></i> Restriction Settings <span class="text-danger">*</span>
            </label>

            <!-- Loop through slots -->
            <div v-for="(slot, index) in currentSchedule.slots" :key="index" class="compact-slot mb-3">
              <!-- Slot Header with Delete Button -->
              <div class="slot-header" v-if="currentSchedule.slots.length > 1">
                <span class="slot-number">Restriction #{{ index + 1 }}</span>
                <button type="button" class="btn-delete-slot" @click="removeSlot(index)">
                  <i class="fas fa-trash-alt"></i>
                </button>
              </div>

              <!-- 1. RESTRICTION MODE -->
              <div>
                <label class="small font-weight-bold d-block mb-1">Restriction Mode</label>
                <div class="compact-mode">
                  <button
                    type="button"
                    class="mode-btn sleep-btn"
                    :class="{ active: slot.mode === 'sleep' }"
                    @click="slot.mode = 'sleep'"
                  >
                    <i class="fas fa-ban"></i> Sleep Mode
                  </button>

                  <button
                    disabled
                    type="button"
                    class="mode-btn limit-btn disabled-mode"
                    :class="{ active: slot.mode === 'limit' }"
                    @click="slot.mode = 'limit'"
                  >
                    <i class="fas fa-hourglass-half"></i> Daily Limit
                    <span class="coming-soon-badge">Coming Soon</span>
                  </button>

                  <button
                    disabled
                    type="button"
                    class="mode-btn apps-btn disabled-mode"
                    :class="{ active: slot.mode === 'apps' }"
                    @click="slot.mode = 'apps'"
                  >
                    <i class="fas fa-mobile-alt"></i> Apps Restriction
                    <span class="coming-soon-badge">Coming Soon</span>
                  </button>
                </div>
              </div>

              <!-- 2. MODE DESCRIPTION / NOTE -->
              <div class="mt-2">
                <div v-if="slot.mode === 'sleep'" class="mode-note mode-note-sleep">
                  <i class="fas fa-info-circle"></i>
                  <span><strong>Sleep Mode:</strong> All apps will be disabled during the selected time period.</span>
                </div>
                <div v-else-if="slot.mode === 'limit'" class="mode-note mode-note-limit">
                  <i class="fas fa-info-circle"></i>
                  <span><strong>Daily Limit:</strong> All apps will be disabled once the total usage time is reached.</span>
                </div>
                <div v-else class="mode-note mode-note-apps">
                  <i class="fas fa-info-circle"></i>
                  <span><strong>Apps Restriction:</strong> Selected app categories will be disabled during the specified time period.</span>
                </div>
              </div>

              <!-- 3. TIME FIELDS (FOR SLEEP MODE AND APPS RESTRICTION) -->
              <div v-if="slot.mode === 'sleep' || slot.mode === 'apps'" class="slot-row mt-3">
                <div class="slot-col">
                  <label class="small font-weight-bold">From</label>
                  <input type="time" class="form-control form-control-sm" v-model="slot.startTime" />
                </div>
                <div class="slot-col">
                  <label class="small font-weight-bold">To</label>
                  <input type="time" class="form-control form-control-sm" v-model="slot.endTime" />
                </div>
              </div>

              <!-- 4. LIMIT INPUT (ONLY FOR DAILY LIMIT MODE) -->
              <div v-if="slot.mode === 'limit'" class="mt-3">
                <label class="small font-weight-bold d-block mb-1">Daily Usage Limit (minutes)</label>
                <input
                  type="number"
                  class="form-control form-control-sm"
                  v-model.number="slot.limit"
                  min="5"
                  max="480"
                  step="5"
                  placeholder="e.g., 60"
                />
                <small class="text-muted d-block mt-1">Minimum 5 minutes, Maximum 480 minutes (8 hours)</small>
              </div>

              <!-- 5. APP CATEGORIES (ONLY FOR APPS RESTRICTION MODE) -->
              <div v-if="slot.mode === 'apps'" class="mt-3">
                <label class="small font-weight-bold d-block mb-2">Select App Categories to Restrict</label>
                <div class="app-categories">
                  <div v-for="category in appCategories" :key="category.id" class="category-wrapper">
                    <!-- Category Header (Clickable) -->
                    <div 
                      class="category-item" 
                      :class="{ 'expanded': expandedCategories[index + '-' + category.id] }"
                      @click="toggleCategoryExpand(index, category.id)"
                    >
                      <div class="category-info">
                        <i :class="category.icon" class="category-icon"></i>
                        <span class="category-name">{{ category.name }}</span>
                        <span class="app-count">({{ category.apps.length }} apps)</span>
                      </div>
                      <div class="category-actions">
                        <div class="custom-control custom-switch" @click.stop>
                          <input 
                            type="checkbox" 
                            class="custom-control-input" 
                            :id="'cat-' + index + '-' + category.id"
                            v-model="slot.categories"
                            :value="category.id"
                          />
                          <label class="custom-control-label" :for="'cat-' + index + '-' + category.id"></label>
                        </div>
                        <i class="fas fa-chevron-down expand-icon" :class="{ 'rotated': expandedCategories[index + '-' + category.id] }"></i>
                      </div>
                    </div>

                    <!-- Apps List (Expandable) -->
                    <div v-show="expandedCategories[index + '-' + category.id]" class="apps-list">
                      <div v-for="app in category.apps" :key="app.id" class="app-item">
                        <span class="app-name">{{ app.name }}</span>
                        <div class="custom-control custom-switch">
                          <input 
                            type="checkbox" 
                            class="custom-control-input" 
                            :id="'app-' + index + '-' + app.id"
                            v-model="slot.restrictedApps"
                            :value="app.packageName"
                          />
                          <label class="custom-control-label" :for="'app-' + index + '-' + app.id"></label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Add More Restriction Button -->
            <button type="button" class="btn-add-more" @click="addSlot()">
              <i class="fas fa-plus-circle"></i> Add More Restriction
            </button>

            <div class="mt-2">
              <small v-if="errors.slots" class="text-danger">{{ errors.slots }}</small>
            </div>
          </div>

          <!-- Schedule Status -->
          <div class="form-group mb-0 text-left">
            <div class="custom-control custom-switch">
              <input
                type="checkbox"
                class="custom-control-input"
                id="scheduleActive"
                v-model="currentSchedule.isActive"
              />
              <label class="custom-control-label font-weight-bold" for="scheduleActive">
                <i class="fas fa-power-off"></i> Activate this schedule immediately
              </label>
            </div>
            <small class="text-muted d-block mt-1">You can enable or disable this schedule anytime from the list</small>
          </div>

        </div>
      </div>

      <!-- Action Buttons -->
      <div class="action-buttons mt-3 d-flex justify-content-end">
        <button
          type="button"
          class="btn btn-secondary btn-sm"
          @click="cancelSchedule()"
          :disabled="isLoading"
        >
          <i class="fas fa-times"></i> Cancel
        </button>

        <button
          type="button"
          class="btn btn-primary btn-sm ml-2"
          @click="saveSchedule()"
          :disabled="!isValidSchedule || isLoading"
        >
          <i class="fas fa-save"></i> {{ isEditMode ? 'Update Schedule' : 'Save Schedule' }}
        </button>
      </div>
    </div>

    <KidFooter :profileId="profileId" :serialNumber="serialNumber" />
  </div>
</template>

<script>
import KidHeader from '@/components/KidHeader.vue'
import KidFooter from '@/components/KidFooter.vue'
import ScheduleService from "../../services/ScheduleService.js"

export default {
  name: 'CreateSchedule',
  components: { KidHeader, KidFooter },
  data() {
    return {
      profileId: this.$route.query.profileId,
      serialNumber: this.$route.query.serialNumber,
      scheduleId: this.$route.query.scheduleId,
      isEditMode: this.$route.query.edit === 'true',
      isLoading: false,
      loadingMessage: 'Processing...',
      conflictWarning: {
        show: false,
        conflicts: []
      },
      warningAlert: {
        show: false,
        warnings: []
      },
      currentSchedule: {
        label: 'Schedule 1',
        type: 'custom',
        days: [],
        slots: [{ startTime: '09:00', endTime: '17:00', mode: 'sleep', limit: 60, categories: [], restrictedApps: [] }],
        isActive: true
      },
      allDays: [
        { label: 'Monday', short: 'Mo', value: 'monday' },
        { label: 'Tuesday', short: 'Tu', value: 'tuesday' },
        { label: 'Wednesday', short: 'We', value: 'wednesday' },
        { label: 'Thursday', short: 'Th', value: 'thursday' },
        { label: 'Friday', short: 'Fr', value: 'friday' },
        { label: 'Saturday', short: 'Sa', value: 'saturday' },
        { label: 'Sunday', short: 'Su', value: 'sunday' }
      ],
      appCategories: [
        { 
          id: 'games', 
          name: 'Games', 
          icon: 'fas fa-gamepad',
          apps: [
            { id: 'game1', name: 'Candy Crush', packageName: 'com.king.candycrushsaga', enabled: false },
            { id: 'game2', name: 'PUBG Mobile', packageName: 'com.tencent.ig', enabled: false },
            { id: 'game3', name: 'Subway Surfers', packageName: 'com.kiloo.subwaysurf', enabled: false },
            { id: 'game4', name: 'Clash of Clans', packageName: 'com.supercell.clashofclans', enabled: false }
          ]
        },
        { 
          id: 'social', 
          name: 'Social Media', 
          icon: 'fas fa-users',
          apps: [
            { id: 'social1', name: 'Facebook', packageName: 'com.facebook.katana', enabled: false },
            { id: 'social2', name: 'Instagram', packageName: 'com.instagram.android', enabled: false },
            { id: 'social3', name: 'Twitter', packageName: 'com.twitter.android', enabled: false },
            { id: 'social4', name: 'TikTok', packageName: 'com.zhiliaoapp.musically', enabled: false }
          ]
        },
        { 
          id: 'entertainment', 
          name: 'Entertainment', 
          icon: 'fas fa-film',
          apps: [
            { id: 'ent1', name: 'YouTube', packageName: 'com.google.android.youtube', enabled: false },
            { id: 'ent2', name: 'Netflix', packageName: 'com.netflix.mediaclient', enabled: false },
            { id: 'ent3', name: 'Spotify', packageName: 'com.spotify.music', enabled: false },
            { id: 'ent4', name: 'Prime Video', packageName: 'com.amazon.avod.thirdpartyclient', enabled: false }
          ]
        },
        { 
          id: 'education', 
          name: 'Education', 
          icon: 'fas fa-graduation-cap',
          apps: [
            { id: 'edu1', name: 'Khan Academy', packageName: 'org.khanacademy.android', enabled: false },
            { id: 'edu2', name: 'Duolingo', packageName: 'com.duolingo', enabled: false },
            { id: 'edu3', name: 'Google Classroom', packageName: 'com.google.android.apps.classroom', enabled: false }
          ]
        },
        { 
          id: 'shopping', 
          name: 'Shopping', 
          icon: 'fas fa-shopping-cart',
          apps: [
            { id: 'shop1', name: 'Amazon', packageName: 'com.amazon.mShop.android.shopping', enabled: false },
            { id: 'shop2', name: 'Flipkart', packageName: 'com.flipkart.android', enabled: false },
            { id: 'shop3', name: 'eBay', packageName: 'com.ebay.mobile', enabled: false }
          ]
        },
        { 
          id: 'news', 
          name: 'News & Magazines', 
          icon: 'fas fa-newspaper',
          apps: [
            { id: 'news1', name: 'Google News', packageName: 'com.google.android.apps.magazines', enabled: false },
            { id: 'news2', name: 'BBC News', packageName: 'bbc.mobile.news.ww', enabled: false },
            { id: 'news3', name: 'CNN', packageName: 'com.cnn.mobile.android.phone', enabled: false }
          ]
        },
        { 
          id: 'productivity', 
          name: 'Productivity', 
          icon: 'fas fa-briefcase',
          apps: [
            { id: 'prod1', name: 'Microsoft Office', packageName: 'com.microsoft.office.officehubrow', enabled: false },
            { id: 'prod2', name: 'Google Drive', packageName: 'com.google.android.apps.docs', enabled: false },
            { id: 'prod3', name: 'Evernote', packageName: 'com.evernote', enabled: false }
          ]
        },
        { 
          id: 'communication', 
          name: 'Communication', 
          icon: 'fas fa-comments',
          apps: [
            { id: 'comm1', name: 'WhatsApp', packageName: 'com.whatsapp', enabled: false },
            { id: 'comm2', name: 'Telegram', packageName: 'org.telegram.messenger', enabled: false },
            { id: 'comm3', name: 'Messenger', packageName: 'com.facebook.orca', enabled: false }
          ]
        }
      ],
      expandedCategories: {},
      errors: {}
    };
  },
  computed: {
    isValidSchedule() {
      return this.currentSchedule.label.trim() !== '' &&
             this.currentSchedule.days.length > 0 &&
             this.currentSchedule.slots.length > 0 &&
             this.validateAllSlots();
    }
  },
  methods: {
    selectScheduleType(type) {
      this.currentSchedule.type = type;
      switch(type) {
        case 'weekday':
          this.currentSchedule.days = ['monday','tuesday','wednesday','thursday','friday'];
          break;
        case 'weekend':
          this.currentSchedule.days = ['saturday','sunday'];
          break;
        case 'everyday':
          this.currentSchedule.days = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
          break;
        case 'custom':
          this.currentSchedule.days = [];
          break;
        default:
          break;
      }
    },
    toggleDay(day) {
      const idx = this.currentSchedule.days.indexOf(day);
      if (idx > -1) this.currentSchedule.days.splice(idx, 1);
      else this.currentSchedule.days.push(day);
      this.currentSchedule.type = 'custom';
    },
    addSlot() {
      this.currentSchedule.slots.push({
        startTime: '09:00',
        endTime: '17:00',
        mode: 'sleep',
        limit: 60,
        categories: [],
        restrictedApps: []
      });
    },
    removeSlot(index) {
      if (this.currentSchedule.slots.length > 1) {
        this.currentSchedule.slots.splice(index, 1);
      }
    },
    toggleCategoryExpand(slotIndex, categoryId) {
      const key = slotIndex + '-' + categoryId;
      this.expandedCategories[key] = !this.expandedCategories[key];
    },
    validateAllSlots() {
      return this.currentSchedule.slots.every(slot => {
        if (slot.mode === 'sleep') {
          return slot.startTime && slot.endTime;
        } else if (slot.mode === 'limit') {
          return slot.limit && slot.limit >= 5;
        } else if (slot.mode === 'apps') {
          return slot.startTime && slot.endTime && slot.categories.length > 0;
        }
        return false;
      });
    },
    validateSchedule() {
      this.errors = {};
      if (!this.currentSchedule.label.trim()) this.errors.label = 'Schedule name is required';
      if (this.currentSchedule.days.length === 0) this.errors.days = 'Please select at least one day';
      
      // Validate each slot
      let hasError = false;
      this.currentSchedule.slots.forEach((slot, index) => {
        if (slot.mode === 'sleep' && (!slot.startTime || !slot.endTime)) {
          this.errors.slots = `Restriction #${index + 1}: Start time and end time are required`;
          hasError = true;
        }
        if (slot.mode === 'limit' && (!slot.limit || slot.limit < 5)) {
          this.errors.slots = `Restriction #${index + 1}: Daily limit must be at least 5 minutes`;
          hasError = true;
        }
        if (slot.mode === 'apps') {
          if (!slot.startTime || !slot.endTime) {
            this.errors.slots = `Restriction #${index + 1}: Start time and end time are required`;
            hasError = true;
          }
          if (slot.categories.length === 0) {
            this.errors.slots = `Restriction #${index + 1}: Please select at least one app category`;
            hasError = true;
          }
        }
      });
      
      return Object.keys(this.errors).length === 0;
    },
    buildSchedulePayload() {
      return {
        profileId: this.profileId,
        serialNumber: this.serialNumber,
        schedule: {
          id: this.isEditMode ? this.scheduleId : null,
          label: this.currentSchedule.label.trim(),
          type: this.currentSchedule.type,
          days: this.currentSchedule.days,
          isActive: this.currentSchedule.isActive,
          restrictions: this.currentSchedule.slots.map((slot, index) => {
            const restriction = {
              order: index + 1,
              mode: slot.mode
            };

            // Add mode-specific fields
            if (slot.mode === 'sleep') {
              restriction.startTime = slot.startTime;
              restriction.endTime = slot.endTime;
            } else if (slot.mode === 'limit') {
              restriction.dailyLimitMinutes = slot.limit;
            } else if (slot.mode === 'apps') {
              restriction.startTime = slot.startTime;
              restriction.endTime = slot.endTime;
              restriction.categories = slot.categories;
              restriction.restrictedApps = slot.restrictedApps || [];
            }

            return restriction;
          })
        }
      };
    },
    async saveSchedule() {
      // Clear previous warnings/errors
      this.conflictWarning.show = false;
      this.warningAlert.show = false;
      
      // Validate form
      if (!this.validateSchedule()) {
        this.$toast.error('Please fix the validation errors before saving');
        return;
      }
      
      // Build payload
      const schedulePayload = this.buildSchedulePayload();
      
      console.log('Schedule Payload:', JSON.stringify(schedulePayload, null, 2));
      
      // Show loading
      this.isLoading = true;
      this.loadingMessage = this.isEditMode ? 'Updating schedule...' : 'Creating schedule...';
      
      try {
        let response;
        
        // Call appropriate API method
        if (this.isEditMode) {
          response = await ScheduleService.updateSchedule(schedulePayload);
        } else {
          response = await ScheduleService.createSchedule(schedulePayload);
        }
        
        // Check response
        if (response.data.success) {
          // Show warnings if any (non-blocking)
          if (response.data.warnings && response.data.warnings.length > 0) {
            this.warningAlert.warnings = response.data.warnings;
            this.warningAlert.show = true;
            
            // Auto-hide warning after 8 seconds
            setTimeout(() => {
              this.warningAlert.show = false;
            }, 8000);
          }
          
          // Show success message
          this.$toast.success(response.data.message || (this.isEditMode ? 'Schedule updated successfully!' : 'Schedule created successfully!'));
          
          // Wait a bit to show warnings if any, then redirect
          setTimeout(() => {
            this.$router.push({ 
              path: '/schedule', 
              query: { 
                profileId: this.profileId, 
                serialNumber: this.serialNumber 
              } 
            });
          }, this.warningAlert.show ? 2000 : 500);
        } else {
          // Handle unexpected success:false
          this.$toast.error(response.data.message || 'Failed to save schedule');
        }
        
      } catch (error) {
        console.error('Error saving schedule:', error);
        
        // Check if it's a conflict error (409)
        if (error.response && error.response.status === 409) {
          const data = error.response.data;
          
          // Show conflicts
          if (data.conflicts && data.conflicts.length > 0) {
            this.conflictWarning.conflicts = data.conflicts;
            this.conflictWarning.show = true;
            
            // Scroll to top to show conflict warning
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
          
          // Show warnings if any
          if (data.warnings && data.warnings.length > 0) {
            this.warningAlert.warnings = data.warnings;
            this.warningAlert.show = true;
          }
          
          this.$toast.error('Cannot save schedule due to conflicts. Please resolve them first.');
        } 
        // Handle validation errors (400)
        else if (error.response && error.response.status === 400) {
          const errorMessage = error.response.data.message || 'Validation error';
          this.$toast.error(errorMessage);
          
          // Try to map error to specific field if possible
          if (errorMessage.toLowerCase().includes('label')) {
            this.errors.label = errorMessage;
          } else if (errorMessage.toLowerCase().includes('days')) {
            this.errors.days = errorMessage;
          } else if (errorMessage.toLowerCase().includes('restriction')) {
            this.errors.slots = errorMessage;
          }
        }
        // Handle other errors
        else {
          const errorMessage = error.response?.data?.message || error.message || 'Failed to save schedule';
          this.$toast.error(errorMessage);
        }
      } finally {
        this.isLoading = false;
      }
    },
    cancelSchedule() {
      if (confirm('Are you sure you want to cancel? Any unsaved changes will be lost.')) {
        this.$router.push({ 
          path: '/schedule', 
          query: { 
            profileId: this.profileId, 
            serialNumber: this.serialNumber 
          } 
        });
      }
    },
    async loadScheduleForEdit() {
      if (!this.isEditMode || !this.scheduleId) return;
      
      this.isLoading = true;
      this.loadingMessage = 'Loading schedule...';
      
      try {
        const response = await ScheduleService.getScheduleById(
          this.scheduleId,
          this.profileId,
          this.serialNumber
        );
        
        if (response.data.success && response.data.data) {
          const schedule = response.data.data;
          
          // Map API data to component data structure
          this.currentSchedule = {
            label: schedule.label,
            type: schedule.type,
            days: schedule.days,
            isActive: schedule.isActive,
            slots: schedule.restrictions.map(restriction => {
              const slot = {
                mode: restriction.mode,
                startTime: restriction.startTime || '09:00',
                endTime: restriction.endTime || '17:00',
                limit: restriction.dailyLimitMinutes || 60,
                categories: restriction.categories || [],
                restrictedApps: restriction.restrictedApps || []
              };
              return slot;
            })
          };
        }
      } catch (error) {
        console.error('Error loading schedule:', error);
        this.$toast.error('Failed to load schedule details');
        
        // Redirect back to list on error
        setTimeout(() => {
          this.$router.push({ 
            path: '/schedule', 
            query: { 
              profileId: this.profileId, 
              serialNumber: this.serialNumber 
            } 
          });
        }, 2000);
      } finally {
        this.isLoading = false;
      }
    }
  },
  mounted() {
    // Open first category by default for first slot
    if (this.currentSchedule.slots.length > 0 && this.appCategories.length > 0) {
      this.expandedCategories['0-' + this.appCategories[0].id] = true;
    }
    
    // Load schedule data if in edit mode
    if (this.isEditMode) {
      this.loadScheduleForEdit();
    }
  }
};
</script>

<style scoped>
/* ---------- Base layout ---------- */
.back-home {
  display: block;
  width: 100%;
  margin-bottom: 12px;
  font-size: 0.95rem;
}

.card {
  border: none;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}

/* ---------- Loading Overlay ---------- */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.loading-overlay p {
  margin-top: 15px;
  font-weight: 600;
  color: #6C63FF;
}

/* ---------- Alert Styles ---------- */
.alert {
  border-radius: 10px;
  border: none;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.alert-danger {
  background: #fee;
  color: #991b1b;
}

.alert-warning {
  background: #fffbeb;
  color: #92400e;
}

.alert-heading {
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 10px;
}

.alert ul {
  padding-left: 20px;
  margin-bottom: 0;
}

.alert li {
  margin-bottom: 5px;
  font-size: 13px;
}

/* ---------- Type Chips ---------- */
.type-chips {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.type-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  border: 1px solid #e6e6ee;
  background: #fafafa;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.18s ease;
  min-width: 92px;
  justify-content: center;
  color: #333;
}

.type-chip i {
  font-size: 14px;
  opacity: 0.9;
}

.type-chip .chip-label {
  font-weight: 600;
  font-size: 13px;
}

.type-chip:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 12px rgba(102,103,255,0.08);
}

.type-chip.active {
  background: linear-gradient(90deg,#6C63FF,#5E54E2);
  color: #fff;
  border-color: rgba(92,80,255,0.9);
  box-shadow: 0 8px 20px rgba(92,80,255,0.12);
}

/* ---------- Days bubbles ---------- */
.days-bubbles {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.day-bubble {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 2px solid #ddd;
  background: #f7f7f7;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  transition: all 0.15s ease;
}

.day-bubble.active {
  background: linear-gradient(135deg,#6C63FF,#5E54E2);
  color: #fff;
  border-color: transparent;
  box-shadow: 0 4px 12px rgba(108,99,255,0.3);
}

.day-bubble:hover {
  transform: translateY(-2px);
}

/* ---------- Compact Slot ---------- */
.compact-slot {
  border-radius: 10px;
  border: 1px solid #eef1fb;
  background: #fff;
  padding: 12px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.05);
  position: relative;
}

.slot-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eef1fb;
}

.slot-number {
  font-weight: 700;
  font-size: 13px;
  color: #6C63FF;
}

.btn-delete-slot {
  background: #fee;
  color: #e11d48;
  border: none;
  padding: 4px 10px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
}

.btn-delete-slot:hover {
  background: #fdd;
}

.slot-row {
  display: flex;
  gap: 12px;
}

.slot-col {
  flex: 1;
}

.slot-col label {
  display: block;
  font-size: 12px;
  margin-bottom: 6px;
  color: #333;
}

/* mode selector */
.compact-mode {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.mode-btn {
  position: relative; /* Allow absolute positioning for badge */
  flex: 1;
  min-width: 120px;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid #e6e6ee;
  background: #fafafa;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.12s ease;
}

.mode-btn.sleep-btn.active {
  background: #ef4444;
  color: #fff;
  border-color: #ef4444;
}

.mode-btn.limit-btn.active {
  background: #f59e0b;
  color: #fff;
  border-color: #f59e0b;
}

.mode-btn.apps-btn.active {
  background: #8b5cf6;
  color: #fff;
  border-color: #8b5cf6;
}

/* Disabled Mode Buttons */
.mode-btn.disabled-mode {
  position: relative;
  opacity: 0.6;
  cursor: not-allowed;
  background: #f5f5f5;
  color: #9ca3af;
  border-color: #e5e7eb;
}

.mode-btn.disabled-mode:hover {
  transform: none;
  background: #f5f5f5;
}

/* Coming Soon Badge */
.coming-soon-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
  font-size: 9px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 6px rgba(245, 158, 11, 0.4);
  animation: pulse-badge 2s infinite;
}

@keyframes pulse-badge {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.9;
  }
}

/* Mode notes/descriptions */
.mode-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 13px;
}

.mode-note i {
  margin-top: 2px;
  flex-shrink: 0;
}

.mode-note-sleep {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
}

.mode-note-limit {
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
}

.mode-note-apps {
  background: #f5f3ff;
  border: 1px solid #ddd6fe;
  color: #6b21a8;
}

/* App Categories */
.app-categories {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.category-wrapper {
  border: 1px solid #eef1fb;
  border-radius: 8px;
  overflow: hidden;
}

.category-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: #fafafa;
  cursor: pointer;
  transition: background 0.2s;
}

.category-item:hover {
  background: #f0f0f5;
}

.category-item.expanded {
  background: #f5f3ff;
  border-bottom: 1px solid #eef1fb;
}

.category-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.category-icon {
  color: #6C63FF;
  font-size: 16px;
  width: 20px;
  text-align: center;
}

.category-name {
  font-size: 13px;
  font-weight: 600;
  color: #333;
}

.app-count {
  font-size: 11px;
  color: #999;
  margin-left: 4px;
}

.category-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.expand-icon {
  color: #6C63FF;
  font-size: 12px;
  transition: transform 0.2s;
}

.expand-icon.rotated {
  transform: rotate(180deg);
}

/* Apps List */
.apps-list {
  background: #fff;
  padding: 8px;
}

.app-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #fafafa;
  border-radius: 6px;
  margin-bottom: 6px;
  border: 1px solid #f0f0f5;
}

.app-item:last-child {
  margin-bottom: 0;
}

.app-name {
  font-size: 13px;
  color: #555;
}

/* Add More Button */
.btn-add-more {
  width: 100%;
  padding: 10px;
  border-radius: 8px;
  border: 2px dashed #6C63FF;
  background: #f5f3ff;
  color: #6C63FF;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;
}

.btn-add-more:hover {
  background: #ede9fe;
  transform: translateY(-2px);
}

/* inputs */
.form-control-sm {
  border-radius: 8px;
  padding: 6px 10px;
}

/* Custom Switch */
.custom-control-label {
  cursor: pointer;
}
</style>